/** Screen sizes in pixels, converted to world units at each vertex's depth so the rig reads the same at any distance. */
export const POINTER_PIXELS = {
    hubRadius: 7, hubSegments: 20, shaftWidth: 6, headWidth: 22, headLength: 18, gap: 10, captionSize: 16, captionGap: 10,
};
/** The rig grows, flies (a critically damped spring of angular frequency flyFrequency) and then reaches out its arms. */
export const POINTER_MOTION = {
    growSeconds: 0.3, extendSeconds: 0.45, retractSeconds: 0.3, flyFrequency: 6, settledPixels: 3,
};
const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const scale = (a, factor) => [a[0] * factor, a[1] * factor, a[2] * factor];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const length = (a) => Math.hypot(a[0], a[1], a[2]);
const normalize = (a) => { const size = length(a); return size > 1e-9 ? scale(a, 1 / size) : [0, 0, 0]; };
const ease = (value) => value * value * (3 - 2 * value);
const clamp01 = (value) => Math.max(0, Math.min(1, value));
/** Box-centred sphere enclosing the points. */
export function enclosePointerPoints(points) {
    if (!points.length)
        return undefined;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const point of points)
        for (let axis = 0; axis < 3; axis++) {
            min[axis] = Math.min(min[axis], point[axis]);
            max[axis] = Math.max(max[axis], point[axis]);
        }
    const center = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    return { center, radius: Math.max(...points.map(point => length(sub(point, center)))) };
}
/** The hub's home lies `depth` of the way from the focus toward the eye, lifted along camera up by `lift` focus radii. */
export function resolvePointerHome(placement, focus, eye, up) {
    return add(add(focus.center, scale(sub(eye, focus.center), placement.depth)), scale(normalize(up), placement.lift * focus.radius));
}
/** Project onto guide segments continuously; fade near spheres instead of jumping to their other side. */
export function sampleGuidePointer(guide, eye) {
    const bounds = enclosePointerPoints(guide.points);
    if (!bounds)
        throw new Error('Guide pointer requires placed guide geometry.');
    let center = guide.points[0];
    let distance = length(sub(center, eye));
    for (let index = 1; index < guide.points.length; index++) {
        const from = guide.points[index - 1], direction = sub(guide.points[index], from);
        const squared = dot(direction, direction);
        if (squared === 0)
            continue;
        const point = add(from, scale(direction, clamp01(dot(sub(eye, from), direction) / squared)));
        const nextDistance = length(sub(point, eye));
        if (nextDistance < distance) {
            center = point;
            distance = nextDistance;
        }
    }
    const visibility = guide.avoid.reduce((visible, sphere) => Math.min(visible, ease(clamp01((length(sub(center, sphere.center)) - sphere.radius) / Math.max(sphere.radius, 1e-6)))), 1);
    return { arm: { key: guide.key, center, radius: 0 }, visibility,
        focus: { center, radius: bounds.radius * 0.12 } };
}
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export function createGuideSlide(guide, eye) {
    const bounds = enclosePointerPoints(guide.points);
    if (!bounds)
        throw new Error('Guide slide requires placed guide geometry.');
    const closed = length(sub(guide.points[0], guide.points.at(-1))) < 1e-8;
    const points = closed ? guide.points.slice(0, -1) : guide.points;
    const safeEdge = (from, to) => guide.avoid.every(sphere => {
        const direction = sub(to, from), squared = dot(direction, direction);
        const closest = add(from, scale(direction, squared ? clamp01(dot(sub(sphere.center, from), direction) / squared) : 0));
        return length(sub(closest, sphere.center)) > sphere.radius * 1.1;
    });
    const candidates = points.map((point, index) => ({ point, index })).filter(({ point }) => safeEdge(point, point));
    if (!candidates.length)
        throw new Error('Guide slide has no unobstructed path.');
    const nearest = candidates.reduce((a, b) => length(sub(a.point, eye)) < length(sub(b.point, eye)) ? a : b);
    const path = [nearest.point];
    for (const direction of [-1, 1]) {
        let index = nearest.index, traveled = 0;
        for (let step = 1; step < points.length; step++) {
            const next = index + direction;
            if (!closed && (next < 0 || next >= points.length))
                break;
            const nextIndex = (next + points.length) % points.length, point = points[nextIndex];
            if (!safeEdge(points[index], point))
                break;
            traveled += length(sub(point, points[index]));
            if (traveled > bounds.radius * 0.6)
                break;
            if (direction < 0)
                path.unshift(point);
            else
                path.push(point);
            index = nextIndex;
        }
    }
    if (path.length < 2)
        throw new Error('Guide slide has no unobstructed path.');
    const focus = enclosePointerPoints(path);
    return { key: guide.key, points: path, focus };
}
export function sampleGuideSlide(slide, progress) {
    const lengths = slide.points.slice(1).map((point, index) => length(sub(point, slide.points[index])));
    let remaining = ease(clamp01(progress)) * lengths.reduce((sum, size) => sum + size, 0);
    for (let index = 0; index < lengths.length; index++) {
        const size = lengths[index];
        if (remaining <= size && size > 0)
            return { key: slide.key, radius: 0,
                center: add(slide.points[index], scale(sub(slide.points[index + 1], slide.points[index]), remaining / size)) };
        remaining -= size;
    }
    return { key: slide.key, radius: 0, center: slide.points.at(-1) };
}
/**
 * An arm ends `gap` outside its target's outline as seen from the camera, pointing at the target's center.
 * The hub is projected along its view ray onto the plane through the target center facing the camera; the tip lies in that plane.
 */
export function pointerArmTip(hub, target, gap, view) {
    if (target.flag || target.outline) {
        const flag = target.flag;
        let up = flag ? normalize(scale(flag.postOffset, -1)) : normalize(view.up);
        if (!length(up))
            up = [0, 1, 0];
        let right = normalize(cross(up, scale(view.forward, -1)));
        if (!length(right))
            right = normalize(cross(view.up, scale(view.forward, -1)));
        const corners = flag ? [[0, -0.5], [1, -0.5], [1, 0.5], [0, 0.5]].map(([x, y]) => add(flag.attachment, add(scale(right, x * flag.width), scale(up, y * flag.height)))) : target.outline;
        const center = enclosePointerPoints(corners).center;
        const normal = view.orthographic ? normalize(view.forward) : normalize(sub(center, view.eye));
        const project = (point) => {
            const ray = view.orthographic ? normal : sub(point, view.eye);
            const denominator = dot(ray, normal);
            if (Math.abs(denominator) < 1e-9)
                throw new Error('Pointer target intersects the camera plane.');
            return add(point, scale(ray, dot(sub(center, point), normal) / denominator));
        };
        const shadow = project(hub);
        let toward = normalize(sub(shadow, center));
        if (!length(toward))
            toward = normalize(sub(view.up, scale(normal, dot(view.up, normal))));
        const horizontal = normalize(cross(view.up, normal)), vertical = normalize(cross(normal, horizontal));
        const xy = (point) => [dot(sub(point, center), horizontal), dot(sub(point, center), vertical)];
        const points = corners.map(point => xy(project(point))).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
        const turn = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
        const half = (input) => {
            const result = [];
            for (const point of input) {
                while (result.length > 1 && turn(result.at(-2), result.at(-1), point) <= 0)
                    result.pop();
                result.push(point);
            }
            return result.slice(0, -1);
        };
        const hull = [...half(points), ...half([...points].reverse())];
        const direction = [dot(toward, horizontal), dot(toward, vertical)];
        let reach = Infinity;
        for (let index = 0; index < hull.length; index++) {
            const a = hull[index], b = hull[(index + 1) % hull.length];
            const edge = [b[0] - a[0], b[1] - a[1]];
            const denominator = direction[0] * edge[1] - direction[1] * edge[0];
            if (Math.abs(denominator) < 1e-9)
                continue;
            const distance = (a[0] * edge[1] - a[1] * edge[0]) / denominator;
            const fraction = (a[0] * direction[1] - a[1] * direction[0]) / denominator;
            if (distance >= 0 && fraction >= 0 && fraction <= 1)
                reach = Math.min(reach, distance);
        }
        if (!Number.isFinite(reach))
            throw new Error('Pointer target outline has no visible edge.');
        return add(center, scale(toward, reach + gap));
    }
    const normal = view.orthographic ? normalize(view.forward) : normalize(sub(target.center, view.eye));
    const ray = view.orthographic ? normal : sub(hub, view.eye);
    const along = dot(ray, normal);
    const shadow = Math.abs(along) > 1e-9 ? add(hub, scale(ray, dot(sub(target.center, hub), normal) / along)) : hub;
    const inPlane = (vector) => sub(vector, scale(normal, dot(vector, normal)));
    let toward = normalize(inPlane(sub(shadow, target.center)));
    if (!length(toward))
        toward = normalize(inPlane(view.up));
    if (!length(toward))
        toward = [0, 1, 0];
    let tip = add(target.center, scale(toward, target.radius + gap));
    // Clip in the camera-facing plane: the pointer is an overlay, so world-depth tests would not prevent wheel overlap.
    for (const obstacle of target.avoid ?? []) {
        const depth = dot(sub(obstacle.center, view.eye), normal);
        const planeDepth = dot(sub(target.center, view.eye), normal);
        if (!view.orthographic && depth <= obstacle.radius)
            return shadow;
        const center = view.orthographic
            ? sub(obstacle.center, scale(normal, dot(sub(obstacle.center, target.center), normal)))
            : add(view.eye, scale(sub(obstacle.center, view.eye), planeDepth / depth));
        const radius = (view.orthographic ? obstacle.radius : obstacle.radius * planeDepth / (depth - obstacle.radius)) + 2 * gap;
        const softness = Math.max(4 * gap, radius * 0.75);
        const outerRadius = radius + softness;
        const direction = sub(tip, shadow);
        const offset = sub(shadow, center);
        const a = dot(direction, direction), b = dot(offset, direction), c = dot(offset, offset) - outerRadius * outerRadius;
        if (a < 1e-12)
            continue;
        if (c <= 0) {
            tip = shadow;
            continue;
        }
        const discriminant = b * b - a * c;
        if (discriminant < 0)
            continue;
        const fraction = (-b - Math.sqrt(discriminant)) / a;
        if (fraction >= 0 && fraction < 1) {
            const perpendicular = Math.sqrt(Math.max(0, dot(offset, offset) - b * b / a));
            const contraction = ease(clamp01((outerRadius - perpendicular) / softness));
            tip = add(shadow, scale(direction, 1 - (1 - fraction) * contraction));
        }
    }
    return tip;
}
export function samplePointerSweep(sweep, progress) {
    const t = ease(clamp01(progress));
    return { key: sweep.key, center: add(sweep.from.center, scale(sub(sweep.to.center, sweep.from.center), t)),
        radius: sweep.from.radius + (sweep.to.radius - sweep.from.radius) * t, avoid: sweep.avoid };
}
export function stepPointerSweep(state, sweep, elapsedMs, running) {
    if (state?.key !== sweep.key)
        return { key: sweep.key, elapsedMs: 0 };
    return { key: sweep.key, elapsedMs: Math.min(sweep.durationMs, state.elapsedMs + (running ? Math.max(0, elapsedMs) : 0)) };
}
/** Exact critically damped spring step toward a fixed home. */
function flyToward(hub, velocity, home, seconds) {
    const omega = POINTER_MOTION.flyFrequency, decay = Math.exp(-omega * seconds);
    const offset = sub(hub, home);
    const drift = add(velocity, scale(offset, omega));
    return {
        hub: add(home, scale(add(offset, scale(drift, seconds)), decay)),
        velocity: scale(sub(velocity, scale(drift, omega * seconds)), decay),
    };
}
/**
 * Advances the rig: dropped arms retract into the hub first; only then does the hub fly to its home, and only once it
 * is there do new arms reach out. Without targets the rig retracts and shrinks away; a vanished rig grows at its home.
 */
export function stepPointerRig(state, input, elapsedSeconds) {
    if (new Set(input.arms.map(arm => arm.key)).size !== input.arms.length)
        throw new Error('Pointer arms require unique keys.');
    const seconds = Math.max(0, elapsedSeconds);
    if (!state) {
        if (!input.arms.length)
            return undefined;
        state = { hub: input.home, velocity: [0, 0, 0], presence: 0, arms: [] };
    }
    const wanted = new Map(input.arms.map(arm => [arm.key, arm]));
    const arms = state.arms.map(arm => {
        const target = wanted.get(arm.key);
        return target ? { ...target, progress: arm.progress, active: true } : { ...arm, active: false };
    });
    for (const arm of input.arms)
        if (!state.arms.some(entry => entry.key === arm.key))
            arms.push({ ...arm, progress: 0, active: true });
    if (arms.some(arm => !arm.active)) {
        const kept = arms.flatMap(arm => {
            if (arm.active)
                return [arm];
            const progress = arm.progress - seconds / POINTER_MOTION.retractSeconds;
            return progress > 0 ? [{ ...arm, progress }] : [];
        });
        return { ...state, velocity: [0, 0, 0], arms: kept };
    }
    if (!input.arms.length) {
        const presence = state.presence - seconds / POINTER_MOTION.growSeconds;
        return presence > 0 ? { ...state, velocity: [0, 0, 0], presence, arms: [] } : undefined;
    }
    const flown = flyToward(state.hub, state.velocity, input.home, seconds);
    const presence = Math.min(1, state.presence + seconds / POINTER_MOTION.growSeconds);
    const reaching = presence === 1 && length(sub(flown.hub, input.home)) <= input.hubTolerance;
    return { ...flown, presence, arms: arms.map(arm => reaching || arm.progress > 0
            ? { ...arm, progress: Math.min(1, arm.progress + seconds / POINTER_MOTION.extendSeconds) } : arm) };
}
export function isPointerRigSettled(state, home, hubTolerance) {
    return state !== undefined && state.presence === 1 && state.arms.length > 0
        && state.arms.every(arm => arm.active && arm.progress === 1) && length(sub(state.hub, home)) <= hubTolerance;
}
/** Billboard basis at a point: right and up span the plane facing the eye. */
function facing(point, view) {
    const normal = normalize(sub(view.eye, point));
    let right = normalize(cross(view.up, normal));
    if (!length(right))
        right = [1, 0, 0];
    return { right, up: cross(normal, right), normal };
}
/** World-space triangles for the hub disc, each arm's shaft and its head, all facing the eye. */
export function buildPointerRigTriangles(state, view) {
    const triangles = [];
    const grown = ease(clamp01(state.presence));
    const hubBasis = facing(state.hub, view);
    const hubRadius = POINTER_PIXELS.hubRadius * view.worldPerPixel(state.hub) * grown;
    if (hubRadius > 0)
        for (let index = 0; index < POINTER_PIXELS.hubSegments; index++) {
            const at = (step) => {
                const angle = step / POINTER_PIXELS.hubSegments * Math.PI * 2;
                return add(state.hub, add(scale(hubBasis.right, Math.cos(angle) * hubRadius), scale(hubBasis.up, Math.sin(angle) * hubRadius)));
            };
            triangles.push(state.hub, at(index), at(index + 1));
        }
    for (const arm of state.arms) {
        const extension = ease(clamp01(arm.progress));
        if (extension <= 0)
            continue;
        const tip = pointerArmTip(state.hub, arm, (arm.gapPixels ?? POINTER_PIXELS.gap) * view.worldPerPixel(arm.center), view);
        const reach = sub(tip, state.hub);
        const direction = normalize(reach);
        const start = add(state.hub, scale(direction, hubRadius));
        const span = length(reach) - hubRadius;
        if (span <= 0)
            continue;
        const end = add(start, scale(direction, span * extension));
        const endPixel = view.worldPerPixel(end);
        const headLength = Math.min(POINTER_PIXELS.headLength * endPixel, span * extension * 0.6);
        const headWidth = headLength * POINTER_PIXELS.headWidth / POINTER_PIXELS.headLength;
        const neck = sub(end, scale(direction, headLength));
        const side = (point) => {
            const across = normalize(cross(direction, facing(point, view).normal));
            return length(across) ? across : facing(point, view).right;
        };
        const startHalf = scale(side(start), POINTER_PIXELS.shaftWidth * view.worldPerPixel(start) / 2);
        const neckHalf = scale(side(neck), POINTER_PIXELS.shaftWidth * view.worldPerPixel(neck) / 2);
        const a = add(start, startHalf), b = sub(start, startHalf), c = add(neck, neckHalf), d = sub(neck, neckHalf);
        triangles.push(a, b, c, b, d, c);
        const headHalf = scale(side(neck), headWidth / 2);
        triangles.push(end, add(neck, headHalf), sub(neck, headHalf));
    }
    return triangles;
}
/** The caption sits just above the hub on screen. */
export function pointerCaptionAnchor(state, view) {
    const pixel = view.worldPerPixel(state.hub);
    return add(state.hub, scale(facing(state.hub, view).up, (POINTER_PIXELS.hubRadius + POINTER_PIXELS.captionGap) * pixel));
}
